import Attachment16 from '@carbon/icons-react/es/attachment/16';
import SendFilled16 from '@carbon/icons-react/es/send--filled/16';
import ViewOff16 from '@carbon/icons-react/es/view--off/16';
import View16 from '@carbon/icons-react/es/view/16';
import { Tooltip, Upload } from 'antd';
import { RcFile } from 'antd/lib/upload';
import { uniqBy } from 'lodash';
import React, { FC, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { UploadFileWithAttachment } from 'api/attachments';
import { ProjectId } from 'api/models';
import { PartnerPermissionName } from 'api/partner/types';
import { Button } from 'components/atoms/button';
import { RichTextEditor } from 'components/atoms/rich-text-editor';
import { ActionBar } from 'components/molecules/action-bar';
import { ProjectVariablesTrigger } from 'components/molecules/variables/variables-select';
import { ContainedList } from 'components/organisms/notifications/list/line-item/contained-list';
import { useProjectDetail } from 'data/projects/hooks';
import { useAccount } from 'hooks/use-account';
import { useUser } from 'hooks/use-user';

const StyledActionBar = styled(ActionBar)`
  padding: 0 var(--spacing-03);
  height: var(--height-lg);
  border-bottom-left-radius: var(--border-radius-small);
  border-bottom-right-radius: var(--border-radius-small);
`;
const CompanyNames = styled.span`
  > span {
    margin-left: 0;
  }
`;

const SendButton = styled(Button)<{ disabled: boolean; private: boolean }>`
  color: var(--white-100);
  background-color: ${(props) =>
    props.disabled
      ? 'var(--gray-30)'
      : props.private
      ? 'var(--orange-40)'
      : 'var(--primary-color)'};
  width: 64px !important;
  border: none;
  &:hover {
    background-color: ${(props) =>
      !props.disabled && !props.private && 'var(--blue-70)'};
  }
`;

const StyledActionBarSecondary = styled(ActionBar.Secondary)`
  gap: var(--spacing-03);
`;

export const CommentBoxActionBar: FC<{
  disableSendBtn: boolean;
  hideAttachments?: boolean;
  hasFileLimitReached: boolean;
  isPrivate: boolean;
  isPrivateReply?: boolean;
  onAddEmoji(emoji): void;
  onFileBeforeUpload?: (
    file: RcFile,
    FileList: RcFile[],
  ) => boolean | Promise<void>;
  onFileUploadRequest({ file }): void;
  onFileUpload({ file }: { file: UploadFileWithAttachment }): void;
  onSend(): void;
  onTogglePrivate(): void;
  hidePrivate?: boolean;
  rte: RichTextEditor | null;
  projectId: ProjectId;
}> = ({
  disableSendBtn,
  hasFileLimitReached,
  isPrivate,
  isPrivateReply,
  onAddEmoji,
  onFileBeforeUpload,
  onFileUploadRequest,
  onFileUpload,
  onSend,
  onTogglePrivate,
  hideAttachments = false,
  hidePrivate = false,
  rte,
  projectId,
}) => {
  const intl = useIntl();
  const { user } = useUser();
  const { data: projectData } = useProjectDetail(projectId);
  const { company: vendorCompany } = useAccount();

  const permissivePartnersInProject = useMemo(
    () =>
      (projectData?.partners ?? []).filter(
        ({ accessLevel }) =>
          accessLevel?.partnerPermissionSetName ===
          PartnerPermissionName.Permissive,
      ),
    [projectData?.partners],
  );
  const sectionVisibleTo = useMemo(
    () =>
      uniqBy(
        [user.company, vendorCompany].concat(permissivePartnersInProject),
        'companyId',
      ).map(({ companyName }) => companyName),
    [user.company, permissivePartnersInProject, vendorCompany],
  );

  return (
    <StyledActionBar private={isPrivate}>
      <ActionBar.Primary>
        {hidePrivate ? null : (
          <Tooltip
            title={
              <FormattedMessage
                id="conversations.tooltips.send_a_message"
                values={{
                  companyName: (
                    <CompanyNames>
                      <ContainedList
                        show={sectionVisibleTo.length}
                        items={sectionVisibleTo}
                      />
                    </CompanyNames>
                  ),
                  private: isPrivate,
                }}
              />
            }
          >
            <Button
              data-cy="chat.visibility.switch"
              disabled={isPrivateReply}
              type="text"
              icon={isPrivate ? <ViewOff16 /> : <View16 />}
              onClick={onTogglePrivate}
            />
          </Tooltip>
        )}
      </ActionBar.Primary>
      <StyledActionBarSecondary>
        <ProjectVariablesTrigger editor={rte?.editor} />
        {hideAttachments ? null : (
          <Upload
            customRequest={onFileUploadRequest}
            multiple
            showUploadList={false}
            onChange={onFileUpload}
            beforeUpload={onFileBeforeUpload}
            data-cy="chat.attach.files.input"
          >
            <Tooltip
              title={intl.formatMessage({
                id: hasFileLimitReached
                  ? 'attachments.add.prevent.max_limit'
                  : 'attachments.add',
              })}
            >
              <Button
                data-cy="chat.attach.files"
                disabled={hasFileLimitReached}
                type="text"
                size="small"
                icon={<Attachment16 />}
              />
            </Tooltip>
          </Upload>
        )}
        <RichTextEditor.EmojiPicker enabled onSelect={onAddEmoji} />
        <Tooltip
          title={intl.formatMessage({
            id: 'conversations.send',
          })}
        >
          <SendButton
            data-cy="chat.send.button"
            disabled={disableSendBtn}
            size="small"
            icon={<SendFilled16 />}
            private={isPrivate}
            onClick={onSend}
          />
        </Tooltip>
      </StyledActionBarSecondary>
    </StyledActionBar>
  );
};
